import { useMemo } from 'react';
import { isEmpty } from 'lodash';

import { VisitId } from '@dreryk/egabinet-visit-published-language-api';
import { PatientConsentDocument, PatientConsentDocumentQuery } from '@dreryk/egabinet-patient-frontend-api';

import { Slot } from '@dreryk/watermelon';
import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateTimeFormatter, PromiseVoidFunction } from '@dreryk/react-common';
import {
   AlignItems,
   createDynamicColumnsWidth,
   JustifyContent,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { visitHistoryTranslationKeys } from '../../translations';

import { ScanDocumentAttachmentLinksCell } from './cells/ScanDocumentAttachmentLinksCell';
import { ScanDocumentCell } from './cells/ScanDocumentCell';

export const usePatientConsentsDataConfiguration = (
   visitId: number,
   patientId: number
): LazyLoadedDataConfigurationByCqrsQuery<PatientConsentDocument> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PatientConsentDocument>(() => {
      const query = new PatientConsentDocumentQuery();
      query.createdOnVisitIds = Slot.of([VisitId.of(visitId)]);
      query.patientId = patientId;
      return query;
   });
};

type UsePatientConsentsColumnsConfigurationOptions = {
   refetchData: PromiseVoidFunction;
};

export const usePatientConsentColumnsConfiguration = (
   options: UsePatientConsentsColumnsConfigurationOptions
): TableColumnConfiguration<PatientConsentDocument>[] => {
   return useMemo<TableColumnConfiguration<PatientConsentDocument>[]>(() => {
      const columnsWidth = createDynamicColumnsWidth({
         name: '100%',
         creationDate: '156px',
         attachments: '203px',
      });

      return [
         {
            name: 'name',
            label: visitHistoryTranslationKeys.patientConsentsTable.name,
            width: columnsWidth.name,
            sortingDisabled: true,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            accessor: 'name',
         },
         {
            name: 'creationDate',
            label: visitHistoryTranslationKeys.patientConsentsTable.creationDate,
            width: columnsWidth.creationDate,
            sortingDisabled: true,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            accessor: 'creationDate',
            formatter: dateTimeFormatter,
         },
         {
            name: 'attachments',
            label: visitHistoryTranslationKeys.patientConsentsTable.scanWithSignature,
            width: columnsWidth.attachments,
            accessor: 'attachments',
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<PatientConsentDocument>) => {
               if (isEmpty(bean.attachments)) {
                  return (
                     <ScanDocumentCell
                        consentDocumentId={bean.id}
                        consentDocumentName={bean.name}
                        onConfirm={options.refetchData}
                     />
                  );
               }

               return (
                  <ScanDocumentAttachmentLinksCell attachments={bean.attachments!} consentDocumentName={bean.name} />
               );
            },
         },
      ];
   }, [options.refetchData]);
};
